/*!
 * Metafield Variant Updater
 * Updates variant-specific metafields when product variants change
 * Handles calculated unit pricing from package size metafields
 */

class MetafieldVariantUpdater extends HTMLElement {
  constructor() {
    super();
    
    this.metafieldBlocks = [];
    this.currentVariantId = null;
    this.productData = null;
    
    // Bind methods
    this.onVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    // Get product data
    this.getProductData();
    
    // Find all metafield blocks
    this.metafieldBlocks = this.querySelectorAll('[data-metafield-block]');
    
    // Listen for variant changes
    this.addEventListener('theme:variant:change', this.onVariantChange);
    
    // Set initial variant ID
    const initialVariantContainer = this.querySelector('[data-variant-id]');
    if (initialVariantContainer) {
      this.currentVariantId = initialVariantContainer.getAttribute('data-variant-id');
    }
  }

  disconnectedCallback() {
    this.removeEventListener('theme:variant:change', this.onVariantChange);
  }

  getProductData() {
    // Try to get product data from the existing JSON script tag
    const productScript = document.querySelector('[data-product-json]');
    if (productScript && productScript.innerHTML) {
      try {
        this.productData = JSON.parse(productScript.innerHTML);
      } catch (e) {
        console.warn('Could not parse product data:', e);
      }
    }
  }

  onVariantChange(event) {
    const variant = event.detail.variant;
    if (!variant || !this.productData) return;

    const newVariantId = variant.id.toString();
    if (this.currentVariantId === newVariantId) return;

    this.currentVariantId = newVariantId;
    this.updateMetafieldBlocks(variant);
  }

  updateMetafieldBlocks(variant) {
    this.metafieldBlocks.forEach(block => {
      // Update the data-variant-id attribute
      block.setAttribute('data-variant-id', variant.id);
      
      // Get the metafield key from the block's parent settings
      const metafieldSpan = block.querySelector('span[class*="kilohinta"], span[class*="meta"]');
      if (!metafieldSpan) return;

      // Get metafield key from the span's class
      const classList = Array.from(metafieldSpan.classList);
      const metafieldKey = classList.find(cls => 
        cls !== 'body-x-small' && 
        cls !== 'body-small' && 
        cls !== 'body-medium' && 
        cls !== 'body-large' && 
        cls !== 'body-x-large'
      );

      if (!metafieldKey || !variant.metafields) return;

      // Look for the metafield value in different possible paths
      let metafieldValue = null;
      
      // Try custom metafields path
      if (variant.metafields.custom && variant.metafields.custom[metafieldKey]) {
        metafieldValue = variant.metafields.custom[metafieldKey].value || variant.metafields.custom[metafieldKey];
      }
      
      // Try direct metafields path
      if (!metafieldValue && variant.metafields[metafieldKey]) {
        metafieldValue = variant.metafields[metafieldKey].value || variant.metafields[metafieldKey];
      }

      // Update the display
      if (metafieldValue) {
        metafieldSpan.textContent = metafieldValue;
        metafieldSpan.style.opacity = '1';
        block.style.display = 'block';
      } else {
        metafieldSpan.textContent = '';
        metafieldSpan.style.opacity = '0';
        // Don't hide the block completely, just make it transparent
      }
    });
  }
}

// Register the custom element
if (!customElements.get('metafield-variant-updater')) {
  customElements.define('metafield-variant-updater', MetafieldVariantUpdater);
}

// Also create a simpler event listener for existing product components
document.addEventListener('DOMContentLoaded', function() {
  const productComponent = document.querySelector('product-component');
  if (!productComponent) return;

  // Listen for variant changes
  productComponent.addEventListener('theme:variant:change', function(event) {
    const variant = event.detail.variant;
    if (!variant) return;

    // Find all metafield containers
    const metafieldContainers = document.querySelectorAll('.selected-variant-data.meta-container[data-variant-id]');
    
    metafieldContainers.forEach(container => {
      const containerVariantId = container.getAttribute('data-variant-id');
      
      if (containerVariantId === variant.id.toString()) {
        // Show the container for the selected variant
        container.style.display = 'block';
      } else {
        // Hide containers for other variants
        container.style.display = 'none';
      }
    });
  });
}); 